import React from "react"
import { motion, AnimatePresence } from 'framer-motion'
import { sideRoutes } from '../Common/Routes'
import { useHistory, useLocation } from "react-router-dom"
import { HiChevronRight, HiOutlineArrowLeftOnRectangle, HiPlus } from "react-icons/hi2"
import { useDispatch, useSelector } from "react-redux"
import { setLoggedIn, setUserDetails } from "../Store/Auth/actions"
import { setSideBar } from "../Store/Theme/actions"
import API from "../Common/API"

const SideBar = () => {
    const history = useHistory()
    const location = useLocation()
    const dispatch = useDispatch()
    const mobileSidebarOpen = useSelector((state) => state.theme.sideBar)
    const userDetails = useSelector((state) => state.auth.userDetails)
    const [desktopOpen, setDesktopOpen] = React.useState(true)
    const [pendingReports, setPendingReports] = React.useState(0)

    React.useEffect(() => {
        const fetchPending = () => {
            API.get("/reports/list?status=pending").then((res) => {
                const count = (res.data?.data || []).length
                setPendingReports(count)
            }).catch(() => {})
        }
        fetchPending()
        const id = setInterval(fetchPending, 60000) // refresh every minute
        return () => clearInterval(id)
    }, [])

    const currentRole = userDetails?.role || ""
    const visibleRoutes = React.useMemo(
        () => sideRoutes.filter((route) => !route.allowedRoles || route.allowedRoles.includes(currentRole)),
        [currentRole]
    )

    const viewMainMenu = (route) => {
        if (route.link === '/reports') setPendingReports(0)
        history.push(route.link)
        dispatch(setSideBar(false))
    }

    const openCreateUser = (event) => {
        event.stopPropagation()
        history.push('/user?add=1')
        dispatch(setSideBar(false))
    }

    const logout = () => {
        localStorage.removeItem('token')
        localStorage.removeItem('accountType')
        dispatch(setUserDetails({}))
        dispatch(setLoggedIn(false))
        dispatch(setSideBar(false))
        window.location.reload()
        history.push('/')
    }

    const nav = (
        <motion.aside
            initial={{ x: -16, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            className={`${desktopOpen ? 'lg:w-[280px]' : 'lg:w-[80px]'} relative flex h-full w-[280px] flex-col bg-gradient-to-b from-sidebar to-sidebarDark px-4 py-6 text-white shadow-2xl transition-all duration-300 lg:fixed lg:left-0 lg:top-0 lg:h-screen`}
        >
            <div className="flex items-center gap-3 border-b px-2 pb-6" style={{borderColor:'rgba(200,146,10,0.2)'}}>
                <div className="shrink-0 rounded-xl overflow-hidden" style={{width:44,height:44,border:'1.5px solid rgba(200,146,10,0.35)',boxShadow:'0 2px 12px rgba(200,146,10,0.2)'}}>
                    <img src="/landing/images/logo.jpeg" alt="Royal Kavery" style={{width:'100%',height:'100%',objectFit:'cover'}} onError={e=>{e.target.style.display='none'}}/>
                </div>
                {desktopOpen && (
                    <div>
                        <h2 className="font-bold" style={{fontSize:15,color:'#F5D264',fontFamily:"'Playfair Display',serif",letterSpacing:'0.02em'}}>Royal Kavery</h2>
                        <p style={{fontSize:11,color:'rgba(245,210,100,0.5)',fontFamily:"'Noto Sans Tamil',sans-serif"}}>Admin Console</p>
                    </div>
                )}
            </div>

            <div className="mt-6">
                {desktopOpen && <p className="px-3 text-xs font-bold uppercase tracking-[0.22em] text-white/50">Workspace</p>}
                <div className="mt-3 space-y-2">
                    {visibleRoutes.map((route) => {
                        const active = location.pathname === route.link || (route.link === '/' && location.pathname === '/')
                        return (
                            <motion.button
                                key={route.link}
                                type="button"
                                whileHover={{ x: 4 }}
                                whileTap={{ scale: 0.98 }}
                                className={`relative flex w-full items-center gap-3 overflow-hidden rounded-lg px-4 py-3 text-left transition duration-200 ${desktopOpen ? '' : 'justify-center px-0'}`}
                                style={active ? {background:'rgba(200,146,10,0.18)',color:'#F5D264',borderLeft:'3px solid #C8920A',boxShadow:'0 4px 16px rgba(200,146,10,0.15)'} : {color:'rgba(255,255,255,0.7)'}}
                                onClick={() => viewMainMenu(route)}
                            >
                                <span className="relative z-10 shrink-0">
                                    <route.TitleIcon className="text-[1.15rem]" />
                                    {route.link === '/reports' && pendingReports > 0 && (
                                        <span className="absolute -top-1 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white">
                                            {pendingReports > 9 ? "9+" : pendingReports}
                                        </span>
                                    )}
                                </span>
                                {desktopOpen && <span className="relative z-10 text-sm font-medium">{route.title}</span>}
                                {desktopOpen && route.link === '/reports' && pendingReports > 0 && (
                                    <span className="relative z-10 ml-auto rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold text-white">
                                        {pendingReports} new
                                    </span>
                                )}
                                {desktopOpen && route.link === '/user' && (
                                    <span
                                        className="relative z-10 ml-auto flex h-8 w-8 items-center justify-center rounded-full bg-white/12 text-white transition hover:bg-white hover:text-primeColor"
                                        title="Add user"
                                        onClick={openCreateUser}
                                    >
                                        <HiPlus className="text-sm" />
                                    </span>
                                )}
                            </motion.button>
                        )
                    })}
                </div>
            </div>

            <div className="mt-auto border-t border-white/10 pt-4">
                <button
                    type="button"
                    className={`flex w-full items-center gap-3 rounded-lg px-4 py-3 text-white/80 transition hover:bg-white/10 hover:text-white ${desktopOpen ? '' : 'justify-center px-0'}`}
                    onClick={logout}
                >
                    <HiOutlineArrowLeftOnRectangle className="text-lg" />
                    {desktopOpen && <span className="text-sm font-medium">Logout</span>}
                </button>
            </div>

            <button
                type="button"
                onClick={() => setDesktopOpen(!desktopOpen)}
                className="absolute -right-3 top-8 hidden h-7 w-7 items-center justify-center rounded-full shadow-lg transition hover:scale-110 lg:flex"
                style={{background:'linear-gradient(135deg,#C8920A,#D4A820)',color:'#060E1A'}}
            >
                <HiChevronRight className={`text-sm transition-transform ${desktopOpen ? 'rotate-180' : ''}`} />
            </button>
        </motion.aside>
    )

    return (
        <>
            <div className={`${desktopOpen ? 'lg:w-[280px]' : 'lg:w-[80px]'} hidden lg:block lg:h-screen lg:shrink-0 transition-all duration-300`}>{nav}</div>
            <AnimatePresence>
                {mobileSidebarOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-40 bg-slate-950/40 px-4 py-4 backdrop-blur-sm lg:hidden"
                        onClick={() => dispatch(setSideBar(false))}
                    >
                        <div className="h-full w-fit" onClick={(event) => event.stopPropagation()}>
                            {nav}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    )
}

export default SideBar
